/* eslint-disable no-unused-vars, react/sort-comp */
import React, { PureComponent, KeyboardEvent, ChangeEvent } from 'react';
import { keyName } from '@bingads-webui/keycode';
import { II18nModel } from './types';

export interface IState {
  currentInputValue: string;
  prevPropsPageNumber: number;
}

// Props interface for Pagination component
export interface IPaginationProps {
  pageSize?: number;
  pageNumber: number;
  itemCount?: number;
  previousIconClassName?: string;
  onPageNumberChanged: (pageNumber: number) => void;
  pageText?: string;
  totalPageCountText?: string;
  nextIconClassName?: string;
  previousPageText?: string;
  nextPageText?: string;
  enableRandomPage?: boolean;
  i18n: II18nModel;
}

function isInteger(n: number): boolean {
  return n === parseInt(String(n), 10);
}

/**
 * A pagination control with previous, next and jump to ramdon page functionabilities
 */

export class Pagination extends PureComponent<IPaginationProps, IState> {
  private textInput: HTMLInputElement = null;

  static defaultProps = {
    pageSize: 20,
    itemCount: 0,
    previousIconClassName: 'glyphicon glyphicon-triangle-left',
    nextIconClassName: 'glyphicon glyphicon-triangle-right',
    pageText: 'Page: ',
    totalPageCountText: ' of ',
    previousPageText: 'Previous page',
    nextPageText: 'Next page',
    enableRandomPage: true,
  };

  state: IState = {
    currentInputValue: (this.props.pageNumber + 1).toString(),
    prevPropsPageNumber: this.props.pageNumber,
  };

  static getDerivedStateFromProps(nextProps: IPaginationProps, prevState: IState): Partial<IState> | null {
    if (nextProps.pageNumber !== prevState.prevPropsPageNumber) {
      return {
        currentInputValue: (nextProps.pageNumber + 1).toString(),
        prevPropsPageNumber: nextProps.pageNumber,
      };
    }
    return null;
  }

  get pageCount(): number {
    return Math.ceil(this.props.itemCount / this.props.pageSize);
  }

  get isFirstPage(): boolean {
    return this.props.pageNumber <= 0;
  }

  get isLastPage(): boolean {
    return this.props.pageNumber >= this.pageCount - 1;
  }

  runIfEnterNext = (e: KeyboardEvent<HTMLButtonElement>): void => {
    this.runIfEnter(e, this.incPageNumber);
  };

  runIfEnterPrev = (e: KeyboardEvent<HTMLButtonElement>): void => {
    this.runIfEnter(e, this.decPageNumber);
  };

  runIfEnter = (event: KeyboardEvent, callback: (...args: any[]) => void, ...restParams: any[]): void => {
    if (event.key === keyName[13]) {
      event.preventDefault();
      callback(...restParams);
    }
  };

  goToPage = (value: number): void => {
    const pageNumber = Math.max(Math.min(value, this.pageCount - 1), 0);

    this.setState({ currentInputValue: (pageNumber + 1).toString() });
    this.props.onPageNumberChanged(pageNumber);
  };

  decPageNumber = (): void => {
    this.goToPage(this.props.pageNumber - 1);
  };

  incPageNumber = (): void => {
    this.goToPage(this.props.pageNumber + 1);
  };

  runIfEnterUnfocus = (e: KeyboardEvent<HTMLInputElement>): void => {
    this.runIfEnter(e, () => { this.textInput.blur(); });
  };

  updatePageNumber = (event: ChangeEvent<HTMLInputElement>): void => {
    const inputValue = event.target.value;
    const intValue = this.props.i18n.parseDecimal(inputValue) - 1;

    if (isInteger(intValue)) {
      this.goToPage(intValue);
    } else {
      this.setState({
        currentInputValue: (this.props.pageNumber + 1).toString(),
      });
    }
  };

  render() {
    return (
      <div className="ba-pagination">
        {/* TODO [lizho] Add common <Button> to handle similar logic */}
        <button
          className="btn btn-default prev"
          type="button"
          onClick={this.decPageNumber}
          onKeyPress={this.runIfEnterPrev}
          disabled={this.isFirstPage}
          tabIndex={this.isFirstPage ? -1 : 0}
          aria-label={this.props.previousPageText}
        >
          <div className={this.props.previousIconClassName} />
        </button>

        <div className="text">
          <span>{this.props.pageText}</span>
        </div>

        <input
          className="pager-input"
          type="text"
          ref={(input) => { this.textInput = input; }}
          value={this.state.currentInputValue}
          onChange={event => this.setState({ currentInputValue: event.target.value })}
          onBlur={this.updatePageNumber}
          onKeyPress={this.runIfEnterUnfocus}
          maxLength={Math.floor(this.pageCount.toString().length)}
          disabled={!this.props.enableRandomPage}
          aria-label={this.props.pageText || 'Page'}
        />

        <div className="text">
          <span>{this.props.totalPageCountText}</span>
          <span className="pager-totalCount">{this.pageCount}</span>
        </div>

        <button
          className="btn btn-default next"
          type="button"
          onClick={this.incPageNumber}
          onKeyPress={this.runIfEnterNext}
          disabled={this.isLastPage}
          tabIndex={this.isLastPage ? -1 : 0}
          aria-label={this.props.nextPageText}
        >
          <div className={this.props.nextIconClassName} />
        </button>
      </div>
    );
  }
}
